import React from 'react'
import {useTranslation} from 'react-i18next'
import moment from 'moment'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Col, Divider, Grid, Row, Tooltip, Typography, Button} from 'antd'
import {PaymentLinkOptionMode} from '@/api'
import {Status} from '@/components/dumb/Status'
import {TagType} from '@/components/dumb/Tag/props'
import {PayByLinkCreateNewLinkStoreSymbol} from '@/pages/PayByLink'
import {Description, MobileBackButton, Icon} from '@/components'
import {ShareButtons} from '@/pages/components'
import {
    LinkDetailsModalHeader,
    NewPaymentLinkWidgetPreview,
    INewPaymentLinkModalStore
} from '@/pages/PayByLink/components'
import {DATE_FORMAT, DATE_TIME_FORMAT, PayByLinkStatus} from '@/constants'
import {getAmountWithCurrentCurrency} from '@/utils'
import {COPIED_PAYMENT_LINK} from '@/constants/playwright-ids'
import {LinkDetailsModalViewProps} from './props'

import styles from './styles.scss'

const {Title, Text} = Typography

export const LinkDetailsModalView: React.FC<LinkDetailsModalViewProps> = observer(
    ({createdPaymentLink, onEmailBtnClick, onSmsBtnClick, onDownloadPdfBtnClick}) => {
        const {t} = useTranslation()
        const screens = Grid.useBreakpoint()
        const isMobile = screens.xs
        const store = useInjection<INewPaymentLinkModalStore>(PayByLinkCreateNewLinkStoreSymbol)

        const linkOptions = createdPaymentLink.linkOptions
        const isMultiUse =
            linkOptions?.mode && linkOptions?.mode !== PaymentLinkOptionMode.ONE_OFF_FIXED
        const isReusableVariable = linkOptions?.mode === PaymentLinkOptionMode.REUSABLE_VARIABLE

        const renderAmountValue = () => {
            if (!isMultiUse || Number(createdPaymentLink.amount) > 0) {
                return (
                    <>
                        {getAmountWithCurrentCurrency(
                            createdPaymentLink.amount,
                            createdPaymentLink.currency
                        )}
                    </>
                )
            }

            return (
                <Status
                    className={styles.status}
                    type={TagType.Default}
                    status={t('PayByLink.open')}
                />
            )
        }

        const mobileDataSource = [
            {
                key: 'type',
                label: t('LinkDetailsModalView.type'),
                value: isMultiUse
                    ? t('NewPaymentLinkModal.labels.multiUse')
                    : t('NewPaymentLinkModal.labels.oneTime')
            },
            {
                key: t('LinkDetailsModalView.amount'),
                label: isReusableVariable
                    ? t('MultiUseForm.labels.presetAmount')
                    : t('LinkDetailsModalView.amount'),

                render: () => renderAmountValue()
            },

            ...(linkOptions?.minAmount
                ? [
                      {
                          key: t('MultiUseForm.labels.minAmount'),
                          label: t('MultiUseForm.labels.minAmount'),
                          value: getAmountWithCurrentCurrency(
                              linkOptions.minAmount,
                              createdPaymentLink.currency
                          )
                      }
                  ]
                : []),

            ...(linkOptions?.maxAmount
                ? [
                      {
                          key: t('MultiUseForm.labels.maxAmount'),
                          label: t('MultiUseForm.labels.maxAmount'),
                          value: getAmountWithCurrentCurrency(
                              linkOptions.maxAmount,
                              createdPaymentLink.currency
                          )
                      }
                  ]
                : []),

            ...(Boolean(createdPaymentLink.customerName)
                ? [
                      {
                          key: t('LinkDetailsModalView.customer'),
                          label: t('LinkDetailsModalView.customer'),
                          value: createdPaymentLink.customerName
                      }
                  ]
                : []),
            {
                key: t('LinkDetailsModalView.dateCreated'),
                label: t('LinkDetailsModalView.dateCreated'),
                value: moment(createdPaymentLink.createdDate).format(DATE_FORMAT)
            },
            {
                key: t('LinkDetailsModalView.expirationDate'),
                label: t('LinkDetailsModalView.expirationDate'),
                value: moment(createdPaymentLink.expirationDate).format(DATE_TIME_FORMAT)
            },
            {
                key: t('LinkDetailsModalView.description'),
                label: t('LinkDetailsModalView.description'),
                value: createdPaymentLink.description
            }
        ]

        return (
            <>
                {isMobile && (
                    <>
                        <MobileBackButton onCancel={store.closeModals} />
                        <Title level={5}>{t('LinkDetailsModalView.linkDetails')}</Title>
                        <div className={styles.mobileHeader}>
                            <Tooltip title={t('LinkDetailsModalView.copied')} trigger={'click'}>
                                <Text
                                    data-test-id={COPIED_PAYMENT_LINK}
                                    className={styles.mobileTitle}
                                    onClick={() => {
                                        navigator.clipboard.writeText(store.paymentLink.url)
                                    }}
                                    strong
                                >
                                    {t('LinkDetailsModalView.copyToClipboard')}
                                </Text>
                            </Tooltip>

                            {isMultiUse && (
                                <>
                                    <Divider className={styles.verticalDivider} type={'vertical'} />

                                    <Button
                                        type='primary'
                                        className={styles.button}
                                        icon={<Icon className={styles.icon} type='qr' />}
                                        onClick={onDownloadPdfBtnClick}
                                    />
                                </>
                            )}

                            <Divider className={styles.verticalDivider} type={'vertical'} />
                            <ShareButtons
                                onEmailClick={onEmailBtnClick}
                                onSmsClick={onSmsBtnClick}
                            />
                        </div>
                        <Divider type={'horizontal'} />
                    </>
                )}
                <LinkDetailsModalHeader
                    createdPaymentLink={createdPaymentLink}
                    onViewAllPaymentsClick={store.viewAllPayments}
                    isMultiUse={isMultiUse}
                />
                <Divider type={'horizontal'} />
                {isMobile && (
                    <Description<string | number>
                        dataSource={
                            createdPaymentLink.initiatorEmail &&
                            createdPaymentLink.initiatorEmail.length
                                ? [
                                      ...mobileDataSource,
                                      {
                                          key: t('LinkDetailsModalView.createdBy'),
                                          label: t('LinkDetailsModalView.createdBy'),
                                          value: createdPaymentLink.initiatorEmail
                                      }
                                  ]
                                : mobileDataSource
                        }
                    />
                )}
                {!isMobile && (
                    <Row gutter={[32, 16]}>
                        <Col xs={24} md={12}>
                            <Row gutter={[16, 16]}>
                                <Col xs={12}>
                                    {isReusableVariable
                                        ? t('MultiUseForm.labels.presetAmount')
                                        : t('LinkDetailsModalView.amount')}
                                </Col>
                                <Col xs={12}>{renderAmountValue()}</Col>

                                <Col xs={12}>{t('LinkDetailsModalView.type')}</Col>
                                <Col xs={12}>
                                    {isMultiUse
                                        ? t('NewPaymentLinkModal.labels.multiUse')
                                        : t('NewPaymentLinkModal.labels.oneTime')}
                                </Col>

                                {linkOptions?.minAmount && (
                                    <>
                                        {' '}
                                        <Col xs={12}>{t('MultiUseForm.labels.minAmount')}</Col>
                                        <Col xs={12}>
                                            {getAmountWithCurrentCurrency(
                                                linkOptions.minAmount,
                                                createdPaymentLink.currency
                                            )}
                                        </Col>
                                    </>
                                )}

                                {linkOptions?.maxAmount && (
                                    <>
                                        {' '}
                                        <Col xs={12}>{t('MultiUseForm.labels.maxAmount')}</Col>
                                        <Col xs={12}>
                                            {getAmountWithCurrentCurrency(
                                                linkOptions.maxAmount,
                                                createdPaymentLink.currency
                                            )}
                                        </Col>
                                    </>
                                )}

                                {Boolean(createdPaymentLink.customerName) && (
                                    <>
                                        <Col xs={12}>{t('LinkDetailsModalView.customer')}</Col>
                                        <Col xs={12}>{createdPaymentLink.customerName}</Col>
                                    </>
                                )}

                                <Col xs={12}>{t('LinkDetailsModalView.dateCreated')}</Col>
                                <Col xs={12}>
                                    {moment(createdPaymentLink.createdDate).format(
                                        DATE_TIME_FORMAT
                                    )}
                                </Col>
                                {createdPaymentLink.expirationDate ? (
                                    <>
                                        <Col xs={12}>
                                            {t('LinkDetailsModalView.expirationDate')}
                                        </Col>
                                        <Col xs={12}>
                                            {moment(createdPaymentLink.expirationDate).format(
                                                DATE_TIME_FORMAT
                                            )}
                                        </Col>
                                    </>
                                ) : null}
                                {createdPaymentLink.viewedDate &&
                                createdPaymentLink.status === PayByLinkStatus.viewed ? (
                                    <>
                                        <Col xs={12}>{t('LinkDetailsModalView.viewedDate')}</Col>
                                        <Col xs={12}>
                                            {moment(createdPaymentLink.viewedDate).format(
                                                DATE_TIME_FORMAT
                                            )}
                                        </Col>
                                    </>
                                ) : null}
                                {createdPaymentLink.paidDate &&
                                createdPaymentLink.status === PayByLinkStatus.paid ? (
                                    <>
                                        <Col xs={12}>{t('LinkDetailsModalView.paidDate')}</Col>
                                        <Col xs={12}>
                                            {moment(createdPaymentLink.paidDate).format(
                                                DATE_TIME_FORMAT
                                            )}
                                        </Col>
                                    </>
                                ) : null}
                                {createdPaymentLink.cancelledDate &&
                                createdPaymentLink.status === PayByLinkStatus.cancelled ? (
                                    <>
                                        <Col xs={12}>{t('LinkDetailsModalView.cancelledDate')}</Col>
                                        <Col xs={12}>
                                            {moment(createdPaymentLink.cancelledDate).format(
                                                DATE_TIME_FORMAT
                                            )}
                                        </Col>
                                    </>
                                ) : null}
                                {createdPaymentLink.attemptedDate &&
                                createdPaymentLink.status === PayByLinkStatus.attempted ? (
                                    <>
                                        <Col xs={12}>{t('LinkDetailsModalView.attemptedDate')}</Col>
                                        <Col xs={12}>
                                            {moment(createdPaymentLink.attemptedDate).format(
                                                DATE_TIME_FORMAT
                                            )}
                                        </Col>
                                    </>
                                ) : null}
                                <Col xs={12}>{t('LinkDetailsModalView.description')}</Col>
                                <Col xs={12}>{createdPaymentLink.description}</Col>
                                {createdPaymentLink.initiatorEmail &&
                                createdPaymentLink.initiatorEmail.length ? (
                                    <>
                                        <Col xs={12}>{t('LinkDetailsModalView.createdBy')}</Col>
                                        <Col xs={12}>{createdPaymentLink.initiatorEmail}</Col>
                                    </>
                                ) : null}
                            </Row>
                        </Col>
                        <Col xs={24} md={12}>
                            <NewPaymentLinkWidgetPreview url={createdPaymentLink.url} isDetails />
                        </Col>
                    </Row>
                )}
            </>
        )
    }
)
